import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ImagePlus, Check } from "lucide-react";

export default function CoverPickerDialog({ trip, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [covers, setCovers] = useState([]);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (open && covers.length === 0) {
      api.get("/covers").then(r => setCovers(r.data.covers)).catch(() => {});
    }
  }, [open, covers.length]);

  const pick = async (url) => {
    setSaving(url);
    try {
      const { data } = await api.patch(`/trips/${trip.trip_id}`, { cover_image: url });
      onUpdate(data); setOpen(false); toast.success("Cover updated");
    } catch { toast.error("Failed"); }
    finally { setSaving(null); }
  };

  if (trip._role === "collaborator" && trip._permission !== "edit") return null;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="text-white border-white/40 bg-white/10 hover:bg-white/20 hover:text-white"
        data-testid="cover-picker-button"
      >
        <ImagePlus size={14} className="mr-1"/> Change cover
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-[#FAF7F2]">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Choose a cover</DialogTitle>
            <p className="text-sm text-stone-500">A curated gallery of magazine-worthy travel photographs.</p>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto">
            {covers.map(c => {
              const isCurrent = trip.cover_image === c.url;
              return (
                <button
                  key={c.id}
                  onClick={() => pick(c.url)}
                  disabled={!!saving}
                  data-testid={`cover-option-${c.id}`}
                  className={`relative rounded-xl overflow-hidden border-2 transition ${isCurrent ? "border-[#C85A32]" : "border-transparent hover:border-stone-300"} group`}
                >
                  <img src={c.url} alt={c.label} className="w-full h-32 object-cover"/>
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-left">
                    <div className="text-[10px] font-mono-code uppercase tracking-widest text-white/90">{c.label}</div>
                  </div>
                  {isCurrent && (
                    <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#C85A32] flex items-center justify-center">
                      <Check size={14} className="text-white"/>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
